import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  Captions,
  ChevronDown,
  ChevronUp,
  Heart,
  HeartOff,
  Maximize,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
  WifiOff,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import {
  actOnMerchantTasteCalibration,
  fetchMerchantTasteCalibration,
  fetchProductionLine,
  type MerchantTasteCalibrationAction,
  type MerchantTasteCalibrationView,
} from '../../merchant/api.js';

const COPY = {
  en: {
    pageTitle: 'Taste Calibration',
    line: 'Project',
    back: 'Business profile',
    language: 'العربية',
    signOut: 'Sign out',
    preparingTitle: 'Preparing your videos',
    preparingBody:
      'Mouja is assembling a focused set for this project. You can leave and come back without losing your setup.',
    refiningTitle: 'Preparing what comes next',
    refiningBody:
      'Mouja is using your choices to prepare the next set. Everything you chose is saved.',
    completeTitle: 'Mouja understands your taste',
    completeBody:
      'Your choices are saved for this project. Your recommendations can now be prepared around them.',
    continueToRecommendations: 'See my recommendations',
    retryLoad: 'Try again',
    loadFailedTitle: 'Taste Calibration is temporarily unavailable',
    loadFailedBody: 'Check your connection and try again. Your saved choices are safe.',
    support:
      'Your choices teach Mouja your creative taste. Keep going — Mouja will tell you when it has enough.',
    like: 'Like this video',
    dislike: 'Dislike this video',
    skip: 'Skip this video',
    previous: 'Return to the previous video',
    play: 'Play',
    pause: 'Pause',
    mute: 'Mute',
    unmute: 'Unmute',
    captions: 'Captions',
    captionsUnavailable: 'Captions are unavailable for this video',
    fullscreen: 'Fullscreen',
    mediaLabel: 'Taste Calibration reference video',
    mediaControls: 'Media controls',
    blocked:
      'Your browser paused playback. Press play to start — nothing was recorded.',
    loadingVideo: 'Loading the next video…',
    replacing: 'This video couldn’t be played. Showing another video.',
    noReplacementTitle: 'Calibration can’t continue right now',
    noReplacementBody:
      'Everything you chose so far is kept. Come back to this project and pick up where you left off.',
    noReplacementAction: 'Back to business profile',
    saveFailed:
      'That choice hasn’t been saved yet. It is still selected, and retrying is safe.',
    retry: 'Retry saving',
    retrying: 'Retrying…',
    offline: 'You’re offline. Reconnecting…',
    reconnected: 'You’re back online. Continue where you left off.',
    educationTitle: 'How to tell Mouja what you like',
    educationBody:
      'Use one simple gesture for each video. You can revisit the previous one and change your mind.',
    educationLike: 'Swipe right to like a video.',
    educationDislike: 'Swipe left if it is not for you.',
    educationSkip: 'Swipe up to skip it.',
    educationPrevious: 'Swipe down to revisit the previous video.',
    gotIt: 'Got it',
    liked: 'Liked',
    disliked: 'Disliked',
    skipped: 'Skipped',
    previousVideo: 'Previous video',
    changedLike: 'Preference changed to Liked',
    changedDislike: 'Preference changed to Disliked',
  },
  ar: {
    pageTitle: 'معايرة الذوق',
    line: 'المشروع',
    back: 'ملف النشاط',
    language: 'English',
    signOut: 'تسجيل الخروج',
    preparingTitle: 'نجهّز فيديوهاتك',
    preparingBody:
      'موجة تجمع لك مجموعة مركّزة للمشروع هذا. تقدر تطلع وترجع بدون ما تفقد إعداداتك.',
    refiningTitle: 'نجهّز الخطوة الجاية',
    refiningBody:
      'موجة تستخدم اختياراتك عشان تجهّز المجموعة الجاية. وكل اختياراتك محفوظة.',
    completeTitle: 'موجة عرفت ذوقك',
    completeBody:
      'اختياراتك انحفظت للمشروع هذا. ونقدر الحين نجهّز توصياتك بناءً عليها.',
    continueToRecommendations: 'ورّني توصياتي',
    retryLoad: 'جرّب مرة ثانية',
    loadFailedTitle: 'معايرة الذوق مو متاحة مؤقتًا',
    loadFailedBody: 'تأكد من اتصالك وجرّب مرة ثانية. واختياراتك المحفوظة بأمان.',
    support: 'اختياراتك تعرّف موجة على ذوقك الإبداعي. كمّل — وموجة بتقول لك لما تكتفي.',
    like: 'أعجبني هذا الفيديو',
    dislike: 'ما عجبني هذا الفيديو',
    skip: 'تخطي هذا الفيديو',
    previous: 'رجوع للفيديو السابق',
    play: 'تشغيل',
    pause: 'إيقاف مؤقت',
    mute: 'كتم الصوت',
    unmute: 'تشغيل الصوت',
    captions: 'الترجمة النصية',
    captionsUnavailable: 'الترجمة النصية مو متاحة لهذا الفيديو',
    fullscreen: 'ملء الشاشة',
    mediaLabel: 'فيديو مرجعي لمعايرة الذوق',
    mediaControls: 'أدوات التحكم بالفيديو',
    blocked: 'المتصفح وقّف التشغيل. اضغط تشغيل عشان يبدأ — وما انسجّل أي شي.',
    loadingVideo: 'نحمّل الفيديو الجاي…',
    replacing: 'ما قدرنا نشغّل هذا الفيديو. راح نعرض لك فيديو ثاني.',
    noReplacementTitle: 'ما نقدر نكمل المعايرة الحين',
    noReplacementBody:
      'كل اللي اخترته لين الحين محفوظ. ارجع للمشروع وكمّل من وين وقفت.',
    noReplacementAction: 'رجوع لملف النشاط',
    saveFailed: 'هذا الاختيار ما انحفظ لين الحين. لسه محدد، وتقدر تعيد المحاولة بأمان.',
    retry: 'جرّب الحفظ مرة ثانية',
    retrying: 'جاري إعادة المحاولة…',
    offline: 'أنت مو متصل. نحاول نرجّع الاتصال…',
    reconnected: 'رجع الاتصال. كمّل من وين وقفت.',
    educationTitle: 'كيف تخبر موجة باللي يعجبك',
    educationBody: 'استخدم حركة بسيطة لكل فيديو. وتقدر ترجع للسابق وتغيّر رأيك.',
    educationLike: 'اسحب يمين إذا عجبك الفيديو.',
    educationDislike: 'اسحب يسار إذا ما ناسبك.',
    educationSkip: 'اسحب فوق عشان تتخطاه.',
    educationPrevious: 'اسحب تحت عشان ترجع للفيديو السابق.',
    gotIt: 'فهمت',
    liked: 'أعجبني',
    disliked: 'ما عجبني',
    skipped: 'تخطيته',
    previousVideo: 'الفيديو السابق',
    changedLike: 'تغيّر التفضيل إلى: أعجبني',
    changedDislike: 'تغيّر التفضيل إلى: ما عجبني',
  },
} as const;

interface CalibrationIntent {
  readonly action: MerchantTasteCalibrationAction;
  readonly expectedCandidateId: string | null;
  readonly expectedLastSequence: number;
  readonly previousDecision: 'LIKE' | 'DISLIKE' | 'SKIP' | null;
  readonly idempotencyKey: string;
}

interface PointerOrigin {
  readonly id: number;
  readonly x: number;
  readonly y: number;
}

function FirstUseDialog({
  copy,
  onDismiss,
}: {
  readonly copy: (typeof COPY)['en'] | (typeof COPY)['ar'];
  readonly onDismiss: () => void;
}): ReactNode {
  const heading = useRef<HTMLHeadingElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    heading.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDismiss();
      } else if (event.key === 'Tab') {
        event.preventDefault();
        button.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onDismiss]);

  return (
    <div className="mj-taste-dialog-backdrop" role="presentation">
      <section
        className="mj-taste-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="taste-education-title"
      >
        <h1 id="taste-education-title" ref={heading} tabIndex={-1}>
          {copy.educationTitle}
        </h1>
        <p>{copy.educationBody}</p>
        <ul>
          <li>
            <Heart aria-hidden="true" /> {copy.educationLike}
          </li>
          <li>
            <HeartOff aria-hidden="true" /> {copy.educationDislike}
          </li>
          <li>
            <ChevronUp aria-hidden="true" /> {copy.educationSkip}
          </li>
          <li>
            <ChevronDown aria-hidden="true" /> {copy.educationPrevious}
          </li>
        </ul>
        <button ref={button} type="button" onClick={onDismiss}>
          {copy.gotIt}
        </button>
      </section>
    </div>
  );
}

export function TasteCalibrationPage(): ReactNode {
  const { productionLineId } = useParams<{ productionLineId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const copy = COPY[language];
  const [view, setView] = useState<MerchantTasteCalibrationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failedIntent, setFailedIntent] = useState<CalibrationIntent | null>(null);
  const [optimisticDecision, setOptimisticDecision] = useState<
    'LIKE' | 'DISLIKE' | 'SKIP' | null
  >(null);
  const [educationOpen, setEducationOpen] = useState(false);
  const educationSeen = useRef(false);
  const interactionLock = useRef(false);
  const playbackFailures = useRef(new Set<string>());
  const playbackRefreshes = useRef(new Set<string>());
  const pointerOrigin = useRef<PointerOrigin | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const mediaFrame = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [captionsAvailable, setCaptionsAvailable] = useState(false);
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [connectionNotice, setConnectionNotice] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const load = useCallback(async (): Promise<boolean> => {
    if (productionLineId === undefined) {
      setLoadFailed(true);
      setLoading(false);
      return false;
    }
    try {
      const next = await fetchMerchantTasteCalibration(productionLineId);
      setView(next);
      setLoadFailed(false);
      return true;
    } catch {
      try {
        const line = await fetchProductionLine(productionLineId);
        if (
          'productionLine' in line &&
          line.capabilities?.tasteCalibration.enabled === false
        ) {
          void navigate(`/production-lines/${encodeURIComponent(productionLineId)}`, {
            replace: true,
          });
          return false;
        }
      } catch {
        // The normal recoverable load error below owns unavailable line reads.
      }
      setLoadFailed(true);
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate, productionLineId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (
      view?.state !== 'NOT_STARTED' &&
      view?.state !== 'PREPARING' &&
      view?.state !== 'AWAITING_ADAPTIVE_SELECTION'
    )
      return undefined;
    const timer = window.setInterval(() => void load(), 4_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [load, view?.state]);

  useEffect(() => {
    if (view?.state === 'READY_TO_START' && !educationSeen.current) {
      setEducationOpen(true);
    }
  }, [view?.state]);

  useEffect(() => {
    const offline = () => {
      setOnline(false);
      setConnectionNotice(copy.offline);
    };
    const reconnected = () => {
      setOnline(true);
      setConnectionNotice(copy.reconnected);
      void load();
    };
    window.addEventListener('offline', offline);
    window.addEventListener('online', reconnected);
    return () => {
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', reconnected);
    };
  }, [copy.offline, copy.reconnected, load]);

  const announcementFor = useCallback(
    (intent: CalibrationIntent): string => {
      if (intent.action === 'LIKE') {
        return intent.previousDecision === 'DISLIKE' ? copy.changedLike : copy.liked;
      }
      if (intent.action === 'DISLIKE') {
        return intent.previousDecision === 'LIKE' ? copy.changedDislike : copy.disliked;
      }
      if (intent.action === 'SKIP') return copy.skipped;
      if (intent.action === 'PREVIOUS') return copy.previousVideo;
      if (intent.action === 'PLAYBACK_FAILED') return copy.replacing;
      return '';
    },
    [copy],
  );

  const performIntent = useCallback(
    async (intent: CalibrationIntent) => {
      if (productionLineId === undefined || interactionLock.current) return;
      interactionLock.current = true;
      setBusy(true);
      setFailedIntent(null);
      try {
        const next = await actOnMerchantTasteCalibration(
          productionLineId,
          {
            action: intent.action,
            expectedCandidateId: intent.expectedCandidateId,
            expectedLastSequence: intent.expectedLastSequence,
          },
          intent.idempotencyKey,
        );
        setView(next);
        setOptimisticDecision(null);
        setAnnouncement(announcementFor(intent));
      } catch {
        setFailedIntent(intent);
        setAnnouncement('');
      } finally {
        interactionLock.current = false;
        setBusy(false);
      }
    },
    [announcementFor, productionLineId],
  );

  const requestAction = useCallback(
    (action: MerchantTasteCalibrationAction) => {
      if (view === null || !online || interactionLock.current) return;
      const candidateId = view.candidate?.candidateId ?? null;
      if (action !== 'START' && candidateId === null) return;
      if (action === 'PREVIOUS' && view.canGoPrevious !== true) return;
      const intent: CalibrationIntent = {
        action,
        expectedCandidateId: action === 'START' ? null : candidateId,
        expectedLastSequence: view.expectedLastSequence,
        previousDecision: view.candidate?.currentDecision ?? null,
        idempotencyKey: newIdempotencyKey(),
      };
      if (action === 'LIKE' || action === 'DISLIKE' || action === 'SKIP') {
        setOptimisticDecision(action);
      }
      void performIntent(intent);
    },
    [online, performIntent, view],
  );

  const dismissEducation = useCallback(() => {
    educationSeen.current = true;
    setEducationOpen(false);
    requestAction('START');
  }, [requestAction]);

  useEffect(() => {
    const candidateId = view?.candidate?.candidateId;
    if (view?.state !== 'PLAYBACK_UNAVAILABLE' || candidateId === undefined) return;
    if (playbackFailures.current.has(candidateId)) return;
    playbackFailures.current.add(candidateId);
    requestAction('PLAYBACK_FAILED');
  }, [requestAction, view?.candidate?.candidateId, view?.state]);

  const failPlayback = useCallback(() => {
    const candidateId = view?.candidate?.candidateId;
    if (candidateId === undefined || playbackFailures.current.has(candidateId)) return;
    if (!playbackRefreshes.current.has(candidateId)) {
      playbackRefreshes.current.add(candidateId);
      void load().then((refreshed) => {
        if (refreshed || playbackFailures.current.has(candidateId)) return;
        playbackFailures.current.add(candidateId);
        requestAction('PLAYBACK_FAILED');
      });
      return;
    }
    playbackFailures.current.add(candidateId);
    requestAction('PLAYBACK_FAILED');
  }, [load, requestAction, view?.candidate?.candidateId]);

  useEffect(() => {
    setPlaying(false);
    setAutoplayBlocked(false);
    setCaptionsAvailable(false);
    setCaptionsEnabled(false);
  }, [view?.candidate?.candidateId]);

  const togglePlayback = useCallback(async () => {
    const element = video.current;
    if (element === null) return;
    if (element.paused) {
      try {
        await element.play();
        setAutoplayBlocked(false);
      } catch {
        setAutoplayBlocked(true);
      }
    } else {
      element.pause();
    }
  }, []);

  const toggleCaptions = useCallback(() => {
    const element = video.current;
    if (element === null || element.textTracks.length === 0) return;
    const next = !captionsEnabled;
    for (const track of Array.from(element.textTracks)) {
      track.mode = next ? 'showing' : 'disabled';
    }
    setCaptionsEnabled(next);
  }, [captionsEnabled]);

  const enterFullscreen = useCallback(() => {
    void mediaFrame.current?.requestFullscreen();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (educationOpen || view?.state !== 'ACTIVE') return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLButtonElement
      )
        return;
      if (event.key === 'ArrowRight') requestAction('LIKE');
      else if (event.key === 'ArrowLeft') requestAction('DISLIKE');
      else if (event.key === 'ArrowUp') requestAction('SKIP');
      else if (event.key === 'ArrowDown') requestAction('PREVIOUS');
      else if (event.key === ' ') void togglePlayback();
      else if (event.key.toLowerCase() === 'm') setMuted((current) => !current);
      else if (event.key.toLowerCase() === 'c') toggleCaptions();
      else if (event.key.toLowerCase() === 'f') enterFullscreen();
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [
    educationOpen,
    enterFullscreen,
    requestAction,
    toggleCaptions,
    togglePlayback,
    view?.state,
  ]);

  const pointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || busy || view?.state !== 'ACTIVE') return;
    pointerOrigin.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // jsdom and older embedded browsers may omit pointer capture. The
      // gesture still resolves from the matching pointer id on pointerup.
    }
  };

  const pointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = pointerOrigin.current;
    pointerOrigin.current = null;
    if (origin === null || origin.id !== event.pointerId || busy) return;
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 56) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      requestAction(dx > 0 ? 'LIKE' : 'DISLIKE');
    } else {
      requestAction(dy < 0 ? 'SKIP' : 'PREVIOUS');
    }
  };

  const decision = optimisticDecision ?? view?.candidate?.currentDecision ?? null;
  const playbackReference = view?.candidate?.playback.reference ?? null;
  const active = view?.state === 'ACTIVE' || view?.state === 'PLAYBACK_UNAVAILABLE';
  const controlsDisabled = busy || !online || view?.state !== 'ACTIVE';
  const retryFailed = () => {
    if (failedIntent !== null) void performIntent(failedIntent);
  };

  const mainContent = useMemo(() => {
    if (loading) {
      return <PreparationState title={copy.preparingTitle} body={copy.preparingBody} />;
    }
    if (loadFailed || view === null) {
      return (
        <MessageState title={copy.loadFailedTitle} body={copy.loadFailedBody}>
          <button type="button" onClick={() => void load()}>
            {copy.retryLoad}
          </button>
        </MessageState>
      );
    }
    if (view.state === 'NO_PLAYABLE_REPLACEMENT') {
      return (
        <MessageState title={copy.noReplacementTitle} body={copy.noReplacementBody}>
          <button type="button" onClick={() => void navigate('/business-profile')}>
            {copy.noReplacementAction}
          </button>
        </MessageState>
      );
    }
    if (view.state === 'COMPLETE') {
      return (
        <MessageState title={copy.completeTitle} body={copy.completeBody}>
          <button
            type="button"
            onClick={() =>
              void navigate(
                view.continuation?.destination ??
                  `/production-lines/${encodeURIComponent(productionLineId ?? '')}/recommendations`,
              )
            }
          >
            {copy.continueToRecommendations}
          </button>
        </MessageState>
      );
    }
    if (view.state === 'AWAITING_ADAPTIVE_SELECTION') {
      return <PreparationState title={copy.refiningTitle} body={copy.refiningBody} />;
    }
    if (view.state === 'NOT_STARTED' || view.state === 'PREPARING') {
      return <PreparationState title={copy.preparingTitle} body={copy.preparingBody} />;
    }
    return null;
  }, [copy, load, loadFailed, loading, navigate, productionLineId, view]);

  return (
    <div className="mj-taste-page" data-surface="dark">
      <a className="mj-skip-link" href="#taste-main">
        {language === 'ar' ? 'تخطّى للمحتوى' : 'Skip to content'}
      </a>

      <div id="taste-main" className="mj-taste-main">
        {mainContent}
        {active || view?.state === 'READY_TO_START' ? (
          <section className="mj-taste-active" aria-label={copy.pageTitle}>
            <div className="mj-taste-page-heading">
              <p>{copy.line}</p>
              <h1>{copy.pageTitle}</h1>
            </div>
            <div className="mj-taste-feed">
              <div
                ref={mediaFrame}
                className={`mj-taste-media${busy ? ' is-advancing' : ''}`}
                onPointerDown={pointerDown}
                onPointerUp={pointerUp}
                onPointerCancel={() => {
                  pointerOrigin.current = null;
                }}
              >
                {playbackReference !== null && view.state === 'ACTIVE' ? (
                  <video
                    ref={video}
                    key={`${view.candidate?.candidateId ?? ''}:${playbackReference}`}
                    src={playbackReference}
                    muted={muted}
                    autoPlay
                    playsInline
                    preload="metadata"
                    aria-label={copy.mediaLabel}
                    onPlay={() => {
                      setPlaying(true);
                    }}
                    onPause={() => {
                      setPlaying(false);
                    }}
                    onCanPlay={() => {
                      const element = video.current;
                      if (element === null) return;
                      void element.play().catch(() => {
                        setAutoplayBlocked(true);
                      });
                    }}
                    onLoadedMetadata={() => {
                      const hasCaptions = (video.current?.textTracks.length ?? 0) > 0;
                      setCaptionsAvailable(hasCaptions);
                    }}
                    onError={failPlayback}
                  />
                ) : (
                  <div className="mj-taste-media-placeholder" aria-hidden="true">
                    <span>
                      {view.state === 'PLAYBACK_UNAVAILABLE'
                        ? copy.replacing
                        : copy.loadingVideo}
                    </span>
                  </div>
                )}

                {autoplayBlocked ? (
                  <button
                    className="mj-taste-play-blocked"
                    type="button"
                    onClick={() => void togglePlayback()}
                  >
                    <Play aria-hidden="true" />
                    <span>{copy.blocked}</span>
                  </button>
                ) : null}

                <div
                  className="mj-taste-media-controls"
                  role="group"
                  aria-label={copy.mediaControls}
                >
                  <button
                    type="button"
                    aria-label={playing ? copy.pause : copy.play}
                    onClick={() => void togglePlayback()}
                  >
                    {playing ? (
                      <Pause aria-hidden="true" />
                    ) : (
                      <Play aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={muted ? copy.unmute : copy.mute}
                    onClick={() => {
                      setMuted((current) => !current);
                    }}
                  >
                    {muted ? (
                      <VolumeX aria-hidden="true" />
                    ) : (
                      <Volume2 aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={
                      captionsAvailable ? copy.captions : copy.captionsUnavailable
                    }
                    aria-pressed={captionsEnabled}
                    disabled={!captionsAvailable}
                    onClick={toggleCaptions}
                  >
                    <Captions aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={copy.fullscreen}
                    onClick={enterFullscreen}
                  >
                    <Maximize aria-hidden="true" />
                  </button>
                </div>

                <div className="mj-taste-navigation-controls">
                  <button
                    type="button"
                    aria-label={copy.skip}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('SKIP');
                    }}
                  >
                    <ChevronUp aria-hidden="true" />
                    <span>{language === 'ar' ? 'تخطي' : 'Skip'}</span>
                  </button>
                  <button
                    type="button"
                    aria-label={copy.previous}
                    disabled={controlsDisabled || view.canGoPrevious !== true}
                    onClick={() => {
                      requestAction('PREVIOUS');
                    }}
                  >
                    <ChevronDown aria-hidden="true" />
                    <span>{language === 'ar' ? 'السابق' : 'Previous'}</span>
                  </button>
                </div>
              </div>

              <aside className="mj-taste-info">
                <div className="mj-taste-desktop-decision-controls">
                  <button
                    type="button"
                    aria-label={copy.previous}
                    disabled={controlsDisabled || view.canGoPrevious !== true}
                    onClick={() => {
                      requestAction('PREVIOUS');
                    }}
                  >
                    <ChevronDown aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={decision === 'DISLIKE' ? 'is-selected' : ''}
                    aria-label={copy.dislike}
                    aria-pressed={decision === 'DISLIKE'}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('DISLIKE');
                    }}
                  >
                    <HeartOff aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={decision === 'LIKE' ? 'is-selected' : ''}
                    aria-label={copy.like}
                    aria-pressed={decision === 'LIKE'}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('LIKE');
                    }}
                  >
                    <Heart aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={copy.skip}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('SKIP');
                    }}
                  >
                    <ChevronUp aria-hidden="true" />
                  </button>
                </div>
                <p>{copy.support}</p>
                <div className="mj-taste-decision-controls">
                  <button
                    type="button"
                    className={decision === 'DISLIKE' ? 'is-selected' : ''}
                    aria-pressed={decision === 'DISLIKE'}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('DISLIKE');
                    }}
                  >
                    <HeartOff aria-hidden="true" />
                    <span>{copy.disliked}</span>
                  </button>
                  <button
                    type="button"
                    className={decision === 'LIKE' ? 'is-selected' : ''}
                    aria-pressed={decision === 'LIKE'}
                    disabled={controlsDisabled}
                    onClick={() => {
                      requestAction('LIKE');
                    }}
                  >
                    <Heart aria-hidden="true" />
                    <span>{copy.liked}</span>
                  </button>
                </div>

                {busy ? (
                  <p className="mj-taste-inline-status" role="status">
                    {copy.loadingVideo}
                  </p>
                ) : null}
                {failedIntent !== null ? (
                  <div className="mj-taste-save-error" role="alert">
                    <p>{copy.saveFailed}</p>
                    <button
                      type="button"
                      disabled={busy || !online}
                      onClick={retryFailed}
                    >
                      <RotateCcw aria-hidden="true" />{' '}
                      {busy ? copy.retrying : copy.retry}
                    </button>
                  </div>
                ) : null}
                {!online || connectionNotice !== null ? (
                  <p className="mj-taste-connection" role="status">
                    {!online ? <WifiOff aria-hidden="true" /> : null}
                    {connectionNotice}
                  </p>
                ) : null}
              </aside>
            </div>
          </section>
        ) : null}
      </div>

      <p className="mj-visually-hidden" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
      {educationOpen ? (
        <FirstUseDialog copy={copy} onDismiss={dismissEducation} />
      ) : null}
    </div>
  );
}

function PreparationState({
  title,
  body,
}: {
  readonly title: string;
  readonly body: string;
}): ReactNode {
  return (
    <section className="mj-taste-state" role="status">
      <span className="mj-taste-state-pulse" aria-hidden="true" />
      <h1>{title}</h1>
      <p>{body}</p>
    </section>
  );
}

function MessageState({
  title,
  body,
  children,
}: {
  readonly title: string;
  readonly body: string;
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <section className="mj-taste-state">
      <h1>{title}</h1>
      <p>{body}</p>
      {children}
    </section>
  );
}
